/** Mean Earth radius in metres -- the same sphere GeoDistanceCalculator's haversine assumes on the backend. */
const EARTH_RADIUS_METRES = 6_371_000;

/**
 * How many segments a circle is drawn with. Azure Maps has no geodesic
 * circle primitive in metres, so a circle is a polygon like any other and
 * this is the only thing that decides whether it looks round. 64 is
 * indistinguishable from a circle at any zoom a jobsite is looked at, and
 * cheap enough to regenerate on every drag of the radius slider.
 */
const CIRCLE_SEGMENTS = 64;

/**
 * The ring of [longitude, latitude] positions that draws a circle of
 * `radiusMetres` around a centre, closed (first point repeated last) the way
 * GeoJSON requires.
 *
 * Computed on the sphere rather than by offsetting degrees, because a degree
 * of longitude is 111 km at the equator and 65 km in northern Spain: the
 * flat version draws a visible egg, and an egg is a fence somebody can be
 * accused of crossing.
 */
export const circleToRing = (
  centerLatitude: number,
  centerLongitude: number,
  radiusMetres: number,
): [number, number][] => {
  const ring: [number, number][] = [];
  const angularDistance = radiusMetres / EARTH_RADIUS_METRES;
  const latitudeRadians = (centerLatitude * Math.PI) / 180;
  const longitudeRadians = (centerLongitude * Math.PI) / 180;

  for (let i = 0; i <= CIRCLE_SEGMENTS; i++) {
    const bearing = (i / CIRCLE_SEGMENTS) * 2 * Math.PI;

    const latitude = Math.asin(
      Math.sin(latitudeRadians) * Math.cos(angularDistance) +
        Math.cos(latitudeRadians) * Math.sin(angularDistance) * Math.cos(bearing),
    );
    const longitude =
      longitudeRadians +
      Math.atan2(
        Math.sin(bearing) * Math.sin(angularDistance) * Math.cos(latitudeRadians),
        Math.cos(angularDistance) - Math.sin(latitudeRadians) * Math.sin(latitude),
      );

    ring.push([(longitude * 180) / Math.PI, (latitude * 180) / Math.PI]);
  }

  return ring;
};

/**
 * Metres between two coordinates -- the same haversine the backend uses, so
 * the distance the screen shows and the distance that decides whether an
 * alert fires can never disagree.
 */
export const distanceMetres = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
  const toRadians = (degrees: number) => (degrees * Math.PI) / 180;
  const dLat = toRadians(lat2 - lat1);
  const dLng = toRadians(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 + Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_METRES * Math.asin(Math.sqrt(a));
};

/**
 * A zoom level that fits a circle of this radius on screen. Rough on
 * purpose: the map is then nudged by the caller, and being one level out is
 * invisible next to opening the map on the wrong continent.
 */
export const zoomForRadius = (radiusMetres: number): number => {
  if (radiusMetres <= 150) return 16;
  if (radiusMetres <= 400) return 15;
  if (radiusMetres <= 900) return 14;
  if (radiusMetres <= 2_000) return 13;
  if (radiusMetres <= 5_000) return 11;
  if (radiusMetres <= 15_000) return 10;
  return 8;
};
